import {cleanCoords, flatten, kinks} from '@turf/turf';
import geojson from 'geojson';
import * as I from 'immutable';
import React from 'react';

import {DrawingMode} from 'app/components/DeclarativeMap/DrawLayer';
import {LibraryDataset, OverlayDataset} from 'app/components/Library/LibraryTypes';
import {HydratedFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganization, ApiOrganizationUser, ApiOverlay} from 'app/modules/Remote/Organization';
import {LoggedInUserActions} from 'app/providers/AuthProvider';
import * as C from 'app/utils/constants';
import {featureM2ToArea} from 'app/utils/featureUtils';
import {UTMArea} from 'app/utils/geoJsonUtils';
import {isFeatureOversized} from 'app/utils/polygonSplit';

import {
  FeatureId,
  ManageFeaturesActions,
  NumberedFeature,
  NumberedFeatureCollection,
  useManageFeatures,
} from './ManageFeaturesProvider';
import ManageFeaturesView from './ManageFeaturesView';

export interface Props {
  organization: I.ImmutableOf<ApiOrganization>;
  profile: I.ImmutableOf<ApiOrganizationUser>;
  loggedInUserActions: LoggedInUserActions;
  firebaseToken: string | null;
}

export interface DrawPropertiesState {
  featureToEdit: geojson.Feature<geojson.Geometry, geojson.GeoJsonProperties> | null;
  setFeatureToEdit: React.Dispatch<
    React.SetStateAction<geojson.Feature<geojson.Geometry, geojson.GeoJsonProperties> | null>
  >;
  editMode: boolean;
  setEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  // This is used by parcel mode - throwing this in here isn't the best, but at least the other modes won't
  // have to know about it and parcel mode is really just a variation on draw
  onMapFeatureClick?: (
    f: geojson.Feature<geojson.Polygon | geojson.MultiPolygon>,
    ev: mapboxgl.MapLayerMouseEvent
  ) => void;
  drawingMode?: DrawingMode;
  setDrawingMode?: React.Dispatch<React.SetStateAction<DrawingMode>>;
  orgOverlays?: ApiOverlay[];
  upstreamParcelOverlays?: LibraryDataset<OverlayDataset>[];
  selectedOverlayId?: number | null;
  setSelectedOverlayId?: (id: number | null) => void;
  selectedOverlayFc?: HydratedFeatureCollection | null;
}

// Module scope keeps the reference stable for useApplyFeatureEdit's dependency array.
const nameNewDrawnProperty = (id: FeatureId) => `New Property ${id}`;

const ManagePropertiesDraw: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  organization,
  profile,
  loggedInUserActions,
  firebaseToken,
}) => {
  const {manageFeaturesState, manageFeaturesActions, loading} = useManageFeatures();

  const [featureToEdit, setFeatureToEdit] = React.useState<null | geojson.Feature>(null);
  const [editMode, setEditMode] = React.useState<boolean>(false);
  const [drawingMode, setDrawingMode] = React.useState<DrawingMode>('draw_polygon');

  const {setUploadedFeatureCollection} = manageFeaturesActions;

  useApplyFeatureEdit(featureToEdit, setUploadedFeatureCollection, nameNewDrawnProperty);

  const drawPropertiesState: DrawPropertiesState = {
    editMode: editMode,
    setEditMode: setEditMode,
    featureToEdit: featureToEdit,
    setFeatureToEdit: setFeatureToEdit,
    drawingMode: drawingMode,
    setDrawingMode: setDrawingMode,
  };

  return (
    <ManageFeaturesView
      profile={profile}
      organization={organization}
      manageFeaturesState={manageFeaturesState}
      manageFeaturesActions={manageFeaturesActions}
      loggedInUserActions={loggedInUserActions}
      mode={'draw'}
      drawPropertiesState={drawPropertiesState}
      loading={loading}
      firebaseToken={firebaseToken}
    />
  );
};

/**
 * Writes a feature the map hands back — dragged vertices, or a newly drawn shape — into the
 * pending collection, re-validating it so __UPSTREAM_PROBLEMS keeps tracking the geometry.
 * Modes that can't create features (agent) omit nameNewFeature, which drops anything that
 * isn't already in the collection.
 */
export const useApplyFeatureEdit = (
  featureToEdit: geojson.Feature | null,
  setUploadedFeatureCollection: ManageFeaturesActions['setUploadedFeatureCollection'],
  nameNewFeature?: (id: FeatureId, feature: geojson.Feature) => string
) => {
  React.useEffect(() => {
    if (!featureToEdit) {
      return;
    }
    setUploadedFeatureCollection((fc) => {
      const featureCollection: NumberedFeatureCollection = fc || {
        type: 'FeatureCollection',
        features: [],
      };
      const featureProblems = validateDrawnProperty(featureToEdit);
      const problems = featureProblems.length ? featureProblems : null;

      if (featureCollection.features.some((f) => f.id === featureToEdit.id)) {
        return {
          ...featureCollection,
          features: featureCollection.features.map((f) =>
            f.id === featureToEdit.id
              ? {
                  ...f,
                  // Keep the other properties: generated features carry a description, and
                  // split pieces a part name, that an edit shouldn't discard.
                  properties: {...f.properties, __UPSTREAM_PROBLEMS: problems},
                  geometry: featureToEdit.geometry,
                }
              : f
          ),
        };
      }

      if (!nameNewFeature) {
        return fc;
      }

      // Count off the last feature's id rather than the length, since features can be deleted.
      const {features} = featureCollection;
      const id = features.length ? features[features.length - 1].id + 1 : 1;
      const newFeature: NumberedFeature = {
        ...featureToEdit,
        id,
        properties: {
          name: nameNewFeature(id, featureToEdit),
          __UPSTREAM_PROBLEMS: problems,
        },
      };
      return {...featureCollection, features: [...features, newFeature]};
    });
    // nameNewFeature belongs here, so callers must pass a stable reference (module scope or
    // useCallback) — an inline arrow would rebuild the collection on every render.
  }, [featureToEdit, setUploadedFeatureCollection, nameNewFeature]);
};

export const checkSelfIntersection = (geometry: geojson.Geometry) => {
  // Skip checking if the geometry is a point
  if (geometry.type === 'Point' || geometry.type === 'MultiPoint') {
    return false;
  }
  // Flatten polygon or multipolygon into a feature collection of polygons to look
  // for self intersections in the polygons. We only care about kinks within
  // an individual polygon, but turf/kinks is more strict and checks intersections
  // between any parts of the multipolygon. Flattening and calling kinks on each polygon
  // lets us be more flexible but still safe here.
  // kinks treats each pair of identical consecutive coordinates as a zero-length degenerate edge,
  // and its segment-intersection algorithm flags those degenerate edges as self-intersections,
  // even when the polygon boundary never actually crosses itself.
  // cleanCoords strips duplicate consecutive vertices to prevent these false positives.
  const flattenedFeatureCollection = flatten(geometry);
  const selfIntersectingPoints = flattenedFeatureCollection.features.reduce(
    (points, f) => points + kinks(cleanCoords(f)).features.length,
    0
  );

  return selfIntersectingPoints > 0;
};

export const validateDrawnProperty = (
  featureToEdit: geojson.Feature<geojson.Geometry, geojson.GeoJsonProperties>
) => {
  // validate drawn properties and surface errors if needed
  const featureProblems: string[] = [];

  // we use this function to validate overlay selections as well, which could be points
  // or linestrings. exit early if that's the case to prevent errors down the line.
  if (!['Polygon', 'MultiPolygon'].includes(featureToEdit.geometry.type)) {
    featureProblems.push(
      `Unsupported geometry type ${featureToEdit.geometry.type}. Only Polygons and MultiPolygons can be added.`
    );
    return featureProblems;
  }

  const featureArea = featureM2ToArea(UTMArea(featureToEdit), {unit: 'areaAcre', exact: true});

  if (featureArea < 0.1) {
    featureProblems.push(
      `Property is too small. Drawn area: ${featureArea.toFixed(3)} acres. Minimum size: 0.1 Acres`
    );
  }

  // Use the same geodesic check the Table and splitter use so all three
  // agree on which features are oversize. Stamping the canonical message
  // here means the existing __UPSTREAM_PROBLEMS-based gates (auto-select,
  // error callout, submit) work without modification, and the Table
  // attaches the Split action by prefix-matching this exact string.
  if (isFeatureOversized(featureToEdit)) {
    featureProblems.push(C.UPLOAD_ERROR_CONSTANTS.TOO_LARGE_AREA.text);
  }

  const featureGeometry: geojson.Polygon | geojson.MultiPolygon = featureToEdit.geometry as
    | geojson.Polygon
    | geojson.MultiPolygon;

  const doesSelfIntersect = checkSelfIntersection(featureGeometry);

  if (doesSelfIntersect) {
    featureProblems.push('Boundary lines cross each other.');
  }

  return featureProblems;
};

export default ManagePropertiesDraw;
